/**
 * Notas de los cobros en efectivo.
 *
 * La observación que se escribe al cargar un cobro cash queda en la DB de
 * cobros y no viaja al recibo: en la orden vemos el nombre de la cuenta
 * destino, no lo que escribió la persona. La Support API la va a buscar a la
 * fuente y acá la pedimos por lote.
 *
 * Todo este módulo es ADICIONAL: si la Support API no contesta, la tarjeta de
 * Cobros tiene que seguir funcionando igual y lo único que pasa es que no se
 * ve este detalle extra.
 */
import { useEffect, useState } from 'react';
import { ApiSalesDashboardService } from 'services';

/** Mismo tope que el endpoint. Se pide de a lotes si hiciera falta. */
const MAX_IDS_POR_PEDIDO = 200;

export interface ICashNote {
	transactionId: number;
	/** Lo que escribió quien cargó el cobro. Null si no escribió nada. */
	notes: string | null;
	createdAt: string | null;
}

interface CashNotesResponse {
	notes: ICashNote[];
	returned: number;
}

/** Notas por `transactionId`. Vacío si no se pudieron traer. */
export type CashNotesMap = Record<number, string>;

const chunk = <T>(items: T[], size: number): T[][] => {
	const out: T[][] = [];
	for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
	return out;
};

/**
 * Trae las notas de un lote de transacciones cash.
 *
 * Envuelve la llamada entera en try/catch, no solo el error HTTP: el
 * interceptor de la Support API *tira* una excepción cuando falta la key o el
 * token de sesión, antes de salir a la red.
 */
async function fetchCashNotes(transactionIds: number[]): Promise<CashNotesMap> {
	const mapa: CashNotesMap = {};

	for (const lote of chunk(transactionIds, MAX_IDS_POR_PEDIDO)) {
		const res = await ApiSalesDashboardService.get<CashNotesResponse>('data/sales/cash-notes', {
			params: { transactionIds: lote.join(',') },
		});
		for (const nota of res.data?.notes ?? []) {
			if (nota.notes) mapa[nota.transactionId] = nota.notes;
		}
	}

	return mapa;
}

/**
 * Notas de los cobros cash de una orden.
 *
 * No sale a pedir nada si la orden no tiene cobros cash — que es la mitad de
 * las órdenes. Cuando sí tiene, es un lookup por clave primaria sobre uno o dos
 * ids en el 72% de los casos.
 *
 * Nunca propaga un error: si algo falla, devuelve el mapa vacío y `failed` en
 * true para que la UI pueda ser honesta sin romper nada.
 */
export function useCashNotes(transactionIds: number[]): { notes: CashNotesMap; failed: boolean } {
	const [notes, setNotes] = useState<CashNotesMap>({});
	const [failed, setFailed] = useState(false);

	/** Clave estable: el array llega con identidad nueva en cada render. */
	const key = transactionIds.join(',');

	useEffect(() => {
		const ids = key
			.split(',')
			.map((v) => Number(v))
			.filter((n) => Number.isInteger(n) && n > 0);

		if (ids.length === 0) {
			setNotes({});
			setFailed(false);
			return;
		}

		let cancelled = false;
		setFailed(false);

		// Envuelto en una promesa propia: fetchCashNotes puede tirar de forma
		// sincrónica desde el interceptor de credenciales.
		Promise.resolve()
			.then(() => fetchCashNotes(ids))
			.then((mapa) => {
				if (!cancelled) setNotes(mapa);
			})
			.catch(() => {
				if (cancelled) return;
				setNotes({});
				setFailed(true);
			});

		return () => {
			cancelled = true;
		};
	}, [key]);

	return { notes, failed };
}
